import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperClass } from "swiper/types";
import { A11y, Keyboard, Mousewheel } from "swiper/modules";
import "swiper/css";
import { useModalContext } from "../context/ModalContext";

/** Base item type for cards */
export interface CardItem {
  id: string;
  title: string;
  subtitle?: string;
  imageUrl?: string;
}

/** Shape returned by your backend */
export interface PageResult<T> {
  items: T[];
  nextCursor?: string | null;
  prevCursor?: string | null;
}

export interface InfiniteSwiperRef {
  slideNext: () => void;
  slidePrev: () => void;
  loadNext: () => Promise<void>;
  loadPrev: () => Promise<void>;
}

interface InfiniteSwiperProps<T extends { id: string; date: string }> {
  initialPageSize?: number;
  fetchPage: (opts: {
    after?: string;
    before?: string;
    limit?: number;
  }) => Promise<PageResult<T>>;
  renderCard: (item: T) => React.ReactNode;
  maxBuffer?: number;
  prefetchThreshold?: number;
  onActiveIndexChange?: (activeItem: T) => void;
  initialSlideIndex?: number;
  externalUpdate?: { id: string; newItem: T } | null;
  onBeforeSlideChange?: (newItem: T, previousItem: T) => boolean | void;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

/** ✅ Fully working Infinite Swiper component */
function InfiniteSwiperInner<T extends { id: string; date: string }>(
  {
    initialPageSize = 8,
    fetchPage,
    renderCard,
    maxBuffer = 40,
    prefetchThreshold = 2,
    onActiveIndexChange,
    initialSlideIndex = 0,
    externalUpdate,
    onBeforeSlideChange,
  }: InfiniteSwiperProps<T>,
  ref: React.Ref<InfiniteSwiperRef>,
) {
  const swiperRef = useRef<SwiperClass | null>(null);
  const ignoreNext = useRef(false);
  const [items, setItems] = useState<T[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null | undefined>(
    undefined,
  );
  const [prevCursor, setPrevCursor] = useState<string | null | undefined>(
    undefined,
  );
  const [loadingNext, setLoadingNext] = useState(false);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAnyModalOpen } = useModalContext();
  const mergeUnique = (base: T[], toAdd: T[], prepend = false) => {
    const seen = new Set(base.map((x) => x.id));
    const filtered = toAdd.filter((x) => !seen.has(x.id));
    return prepend ? [...filtered, ...base] : [...base, ...filtered];
  };

  // ✅ Fixed buffer trimming logic
  const trimToBuffer = (arr: T[], centerIndex: number) => {
    if (arr.length <= maxBuffer) return { trimmed: arr, offsetDelta: 0 };

    const half = Math.floor(maxBuffer / 2);
    const maxStart = Math.max(0, arr.length - maxBuffer);
    const start = Math.min(Math.max(0, centerIndex - half), maxStart);
    const end = start + maxBuffer;
    const trimmed = arr.slice(start, end);
    const offsetDelta = start;

    return { trimmed, offsetDelta };
  };

  useImperativeHandle(ref, () => ({
    slideNext: () => swiperRef.current?.slideNext(),
    slidePrev: () => swiperRef.current?.slidePrev(),
    loadNext,
    loadPrev,
  }));

  useEffect(() => {
    if (!swiperRef.current) return;

    const s = swiperRef.current;

    if (isAnyModalOpen) {
      s.allowSlideNext = false;
      s.allowSlidePrev = false;
      s.allowTouchMove = false;
      s.keyboard.disable();
      s.mousewheel.disable();
    } else {
      s.allowSlideNext = true;
      s.allowSlidePrev = true;
      s.allowTouchMove = true;
      s.keyboard.enable();
      s.mousewheel.enable();
    }
  }, [isAnyModalOpen]);

  useEffect(() => {
    if (!externalUpdate) return;
    setItems((prev) =>
      prev.map((i) =>
        i.id === externalUpdate.id ? externalUpdate.newItem : i,
      ),
    );
  }, [externalUpdate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchPage({ limit: initialPageSize });
        if (cancelled) return;
        setItems(res.items ?? []);
        setNextCursor(res.nextCursor ?? null);
        setPrevCursor(res.prevCursor ?? null);
        setError(null);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? "Failed to load items");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchPage, initialPageSize]);

  const loadNext = async () => {
    if (loadingNext || !nextCursor) return;
    if (items.some((i) => i.date >= nextCursor)) return;
    setLoadingNext(true);
    try {
      const res = await fetchPage({
        after: nextCursor,
        limit: initialPageSize,
      });
      setItems((prev) => mergeUnique(prev, res.items ?? []));
      setNextCursor(res.nextCursor ?? null);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load more");
    } finally {
      setLoadingNext(false);
    }
  };

  const loadPrev = async () => {
    if (loadingPrev || !prevCursor) return;
    if (items.some((i) => i.date <= prevCursor)) return;
    setLoadingPrev(true);
    const active = swiperRef.current?.activeIndex ?? 0;
    try {
      const res = await fetchPage({
        before: prevCursor,
        limit: initialPageSize,
      });
      const addedCount = res.items?.length ?? 0;
      setItems((prev) => mergeUnique(prev, res.items ?? [], true));
      setPrevCursor(res.prevCursor ?? null);

      // adjust index after prepend
      requestAnimationFrame(() => {
        const s = swiperRef.current;
        if (s && addedCount > 0 && !s.destroyed) {
          // ✅ Added destroyed check
          s.slideTo(active + addedCount, 0);
        }
      });
    } catch (e: any) {
      setError(e?.message ?? "Failed to load previous");
    } finally {
      setLoadingPrev(false);
    }
  };

  const handleSlideChange = async () => {
    const s = swiperRef.current;
    if (!s) return;
    const idx = s.activeIndex;

    // ✅ Call the active index change callback
    if (onActiveIndexChange && items[idx]) {
      onActiveIndexChange(items[idx]);
    }

    if (idx >= items.length - 1 - prefetchThreshold) void loadNext();
    if (idx <= prefetchThreshold) void loadPrev();

    if (items.length > maxBuffer * 1.25) {
      const { trimmed, offsetDelta } = trimToBuffer(items, idx);
      const newIndex = idx - offsetDelta;
      setItems(trimmed);
      requestAnimationFrame(() => {
        const s = swiperRef.current;
        if (s && !s.destroyed) {
          // ✅ Added destroyed check
          s.slideTo(clamp(newIndex, 0, trimmed.length - 1), 0);
        }
      });
    }
  };

  const slides = useMemo(
    () =>
      items.map((item) => (
        <SwiperSlide key={item.id}>{renderCard(item)}</SwiperSlide>
      )),
    [items, renderCard],
  );

  return (
    <div className="w-full max-w-5xl mx-auto">
      <Swiper
        modules={[A11y, Keyboard, Mousewheel]}
        onSwiper={(s) => (swiperRef.current = s)}
        onSlideChange={(swiper) => {
          if (ignoreNext.current) {
            ignoreNext.current = false;
            return;
          }
          const idx = swiper.activeIndex;
          const prevIdx = swiper.previousIndex;
          const newItem = items[idx];
          const prevItem = items[prevIdx];

          // 🧠 Run the before-change hook (only if both slides exist)
          if (
            newItem &&
            prevItem &&
            typeof onBeforeSlideChange === "function"
          ) {
            const allow = onBeforeSlideChange(newItem, prevItem);
            if (allow === false) {
              // 🚫 Prevent recursive call by setting flag
              ignoreNext.current = true;
              swiper.slideTo(prevIdx, 0, false); // instantly revert
              return;
            }
          }

          handleSlideChange();
        }}
        onReachEnd={() => void loadNext()}
        onReachBeginning={() => void loadPrev()}
        keyboard={{ enabled: true }}
        mousewheel={{ forceToAxis: true }}
        a11y={{ enabled: true }}
        spaceBetween={60}
        slidesPerView={1}
        centeredSlides
        initialSlide={initialSlideIndex ?? 0}
        resistanceRatio={0.85}
        grabCursor
        className="!pb-6"
        breakpoints={{
          640: { slidesPerView: 1.05, spaceBetween: 60 },
          768: { slidesPerView: 1.08, spaceBetween: 60 },
          1024: { slidesPerView: 1.12, spaceBetween: 60 },
        }}
      >
        {slides}
      </Swiper>

      <div className="flex items-center justify-center gap-3 text-sm text-neutral-500 mt-2">
        {loadingPrev && <span>Laster tidligere…</span>}
        {loadingNext && <span>Laster flere…</span>}
        {error && <span className="text-red-500">{error}</span>}
      </div>
    </div>
  );
}

// Make the forwardRef result callable with a generic type argument:
const InfiniteSwiper = forwardRef(InfiniteSwiperInner) as <
  T extends { id: string; date: string },
>(
  props: InfiniteSwiperProps<T> & React.RefAttributes<InfiniteSwiperRef>,
) => React.ReactElement;

// Default export
export default InfiniteSwiper;
